/**
 * Admin Usage Logs Page — filterable LLM usage log viewer.
 */

import React, { useEffect, useState, useCallback } from 'react';
import * as adminService from '../../services/admin.service';
import { ChevronRight, AlertCircle, Search, ToggleLeft, ToggleRight } from 'lucide-react';

export default function AdminUsageLogsPage() {
  const [items, setItems] = useState<adminService.UsageLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nextCursor, setNextCursor] = useState<string | undefined>();

  // Filters
  const [filterDevId, setFilterDevId] = useState('');
  const [appliedDevId, setAppliedDevId] = useState<string | undefined>();

  // Name resolution toggle
  const [showNames, setShowNames] = useState(false);
  const [devNames, setDevNames] = useState<Record<string, string>>({});
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const [namesLoaded, setNamesLoaded] = useState(false);

  const loadLogs = useCallback(
    (cursor?: string, devId?: string) => {
      setLoading(true);
      adminService
        .getUsageLogs({
          developerId: devId || undefined,
          limit: 50,
          cursor,
        })
        .then((result) => {
          setItems((prev) => {
            const merged = cursor ? [...prev, ...result.items] : result.items;
            return [...merged].sort((a, b) => {
              const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
              const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
              return tb - ta;
            });
          });
          setNextCursor(result.nextCursor);
        })
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    },
    []
  );

  useEffect(() => {
    loadLogs(undefined, appliedDevId);
  }, [loadLogs, appliedDevId]);

  const handleSearch = () => {
    setItems([]);
    setNextCursor(undefined);
    setAppliedDevId(filterDevId.trim() || undefined);
  };

  const handleClear = () => {
    setFilterDevId('');
    setItems([]);
    setNextCursor(undefined);
    setAppliedDevId(undefined);
  };

  // Load name maps when toggle is turned on
  const handleToggleNames = async () => {
    if (!showNames && !namesLoaded) {
      try {
        const [devResult, userResult] = await Promise.all([
          adminService.listDevelopers(200),
          adminService.listUsers(200),
        ]);
        const dMap: Record<string, string> = {};
        for (const d of devResult.developers) {
          dMap[d.developerId] = d.name || d.email;
        }
        const uMap: Record<string, string> = {};
        for (const u of userResult.users) {
          uMap[u.userId] = u.fullName || u.email;
        }
        setDevNames(dMap);
        setUserNames(uMap);
        setNamesLoaded(true);
      } catch {
        setError('Failed to load names');
        return;
      }
    }
    setShowNames((prev) => !prev);
  };

  const resolveDev = (id: string) =>
    showNames && devNames[id] ? devNames[id] : `${id.substring(0, 8)}…`;

  const resolveUser = (id: string) =>
    showNames && userNames[id] ? userNames[id] : `${id.substring(0, 8)}…`;

  return (
    <div className="apikeys-page">
      <div className="page-header">
        <div>
          <h1>Usage Logs</h1>
          <p>LLM request history across all developers</p>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={16} /> {error}
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setError('')}
            style={{ marginLeft: 'auto' }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="key-form-row" style={{ marginBottom: 16 }}>
        <div className="form-group" style={{ flex: 1, maxWidth: 300 }}>
          <label>Developer ID</label>
          <input
            placeholder="Filter by developer ID"
            value={filterDevId}
            onChange={(e) => setFilterDevId(e.target.value)}
          />
        </div>
        <button className="btn btn-accent" onClick={handleSearch}>
          <Search size={16} /> Search
        </button>
        {appliedDevId && (
          <button className="btn btn-ghost" onClick={handleClear}>
            Clear
          </button>
        )}
        <button
          className={`btn ${showNames ? 'btn-accent' : 'btn-ghost'}`}
          onClick={handleToggleNames}
          title="Show names instead of IDs"
          style={{ marginLeft: 'auto' }}
        >
          {showNames ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
          {' '}Names
        </button>
      </div>

      {/* Logs table */}
      <div className="table-container">
        <table className="data-table" style={{ fontSize: 13 }}>
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Developer</th>
              <th>Model</th>
              <th>Tokens</th>
              <th>Latency</th>
              <th>User</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={`${item.requestId}-${idx}`}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {item.timestamp
                    ? new Date(item.timestamp).toLocaleString()
                    : '—'}
                </td>
                <td>
                  {showNames ? (
                    <span style={{ fontSize: 12 }}>{resolveDev(item.developerId)}</span>
                  ) : (
                    <code style={{ fontSize: 11 }}>{resolveDev(item.developerId)}</code>
                  )}
                </td>
                <td>{item.model || '—'}</td>
                <td className="cell-primary">
                  {item.tokensDeducted ? Number(item.tokensDeducted).toLocaleString() : '—'}
                </td>
                <td>{item.latencyMs ? `${item.latencyMs}ms` : '—'}</td>
                <td>
                  {item.userId ? (
                    showNames ? (
                      <span style={{ fontSize: 12 }}>{resolveUser(item.userId)}</span>
                    ) : (
                      <code style={{ fontSize: 11 }}>{resolveUser(item.userId)}</code>
                    )
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  {item.error ? (
                    <span style={{ color: 'var(--danger)' }}>Error</span>
                  ) : (
                    <span style={{ color: 'var(--success)' }}>OK</span>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && !loading && (
              <tr>
                <td colSpan={7} className="empty-state">
                  No usage logs found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {loading && <div className="page-loader">Loading…</div>}

      {nextCursor && !loading && (
        <div style={{ textAlign: 'center', padding: '16px' }}>
          <button
            className="btn btn-ghost"
            onClick={() => loadLogs(nextCursor, appliedDevId)}
          >
            Load More <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
